import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { listObjekte, type ObjektState } from "@/api/objekte";
import { PhaseBadge } from "@/components/PhaseBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { formatEur, formatRelative } from "@/lib/format";

export function ObjektePage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["objekte"],
    queryFn: () => listObjekte(),
    refetchOnMount: true,
  });

  return (
    <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Objekte</h1>
          <p className="text-sm text-muted-foreground">
            Bekannte Anlageobjekte aus dem Repo (
            <code className="text-xs">objekte/&lt;slug&gt;/</code>).
          </p>
        </div>
      </div>

      {isLoading && <ObjekteLoading />}

      {isError && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-6"
          data-testid="objekte-error"
        >
          <p className="text-sm font-medium text-destructive">
            Objekte konnten nicht geladen werden.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {error instanceof Error ? error.message : "Unbekannter Fehler."}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void refetch()}
          >
            Erneut versuchen
          </Button>
        </div>
      )}

      {data && data.objekte.length === 0 && (
        <div
          className="rounded-lg border p-10 text-center"
          data-testid="objekte-empty"
        >
          <p className="text-sm font-medium">Noch keine Objekte angelegt.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Lege ein Objekt via{" "}
            <code className="text-xs">/neues-objekt &lt;URL&gt;</code> oder via
            der Inserate-Triage an.
          </p>
        </div>
      )}

      {data && data.objekte.length > 0 && (
        <ul
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="objekte-grid"
        >
          {data.objekte.map((o) => (
            <ObjektCard key={o.slug} objekt={o} />
          ))}
        </ul>
      )}
    </main>
  );
}

function ObjektCard({ objekt }: { objekt: ObjektState }) {
  return (
    <li>
      <Link
        to="/objekte/$slug"
        params={{ slug: objekt.slug }}
        className="block rounded-lg border p-4 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`objekt-card-${objekt.slug}`}
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-mono text-sm font-medium leading-tight">
            {objekt.slug}
          </h3>
          <PhaseBadge phase={objekt.phase} />
        </div>
        {objekt.address_short && (
          <p className="mt-2 text-sm text-muted-foreground">
            {objekt.address_short}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="font-medium tabular-nums">
            {formatEur(objekt.kaufpreis_eur)}
          </span>
          <span className="text-muted-foreground">
            {formatRelative(objekt.last_updated)}
          </span>
        </div>
      </Link>
    </li>
  );
}

function ObjekteLoading() {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="rounded-lg border p-4 space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </li>
      ))}
    </ul>
  );
}
